import { useEffect, useState, type ReactNode } from 'react'
import {
  Bot,
  CheckCircle2,
  Clock3,
  KeyRound,
  Lock,
  Mail,
  MessageCircle,
  ShieldCheck,
  User,
  UsersRound,
  X,
} from 'lucide-react'
import { CONSOLE_PASSWORD_RULE_MESSAGE, isValidConsolePassword } from '@wxproto-desk/shared'
import { apiPost } from '@/lib/api'
import { clearStoredReferral, readReferralFromUrl, readStoredReferral } from '@/lib/referral'
import {
  validateConfirmPasswordField,
  validateEmailField,
  validatePasswordField,
  validateUsernameField,
} from '@/lib/field-validation'
import type { ConsoleUser } from '@/types'
import { message } from '@/components/Toast'
import { PasswordInput } from '@/components/PasswordInput'
import { CopyableSupportNumber, SUPPORT_QQ, SUPPORT_WECHAT_ID } from '@/components/SupportContact'

type UsernameCheckResponse = { success: boolean; available: boolean }

type Mode = 'login' | 'bootstrap'
type RegisterForm = {
  username: string
  password: string
  confirmPassword: string
  email: string
  emailCode: string
  referralCode: string
}
type RegisterField = keyof RegisterForm
type RegisterErrors = Partial<Record<RegisterField, string>>
type ForgotForm = {
  username: string
  email: string
  emailCode: string
}
type AuthResponse = { success: boolean; user: ConsoleUser }
type RegisterCodeResponse = { success: boolean; devCode?: string }
type ForgotPasswordResponse = { success: boolean; devPassword?: string }

const PASSWORD_PLACEHOLDER = '8-15 位，含大小写字母和数字'
const USERNAME_PLACEHOLDER = '字母开头，3-10 位字母或数字'

const emptyRegisterForm: RegisterForm = {
  username: '',
  password: '',
  confirmPassword: '',
  email: '',
  emailCode: '',
  referralCode: '',
}

const REFERRAL_CODE_PATTERN = /^[A-Z0-9]{4,16}$/

// 邀请链接带来的邀请码作为预填值，用户仍可手动改写或清空。
function initialRegisterForm(): RegisterForm {
  return { ...emptyRegisterForm, referralCode: readStoredReferral() || readReferralFromUrl() }
}

const emptyForgotForm: ForgotForm = {
  username: '',
  email: '',
  emailCode: '',
}

function validateRegisterField(field: RegisterField, form: RegisterForm): string {
  switch (field) {
    case 'username':
      return validateUsernameField(form.username)
    case 'password':
      return validatePasswordField(form.password)
    case 'confirmPassword':
      return validateConfirmPasswordField(form.password, form.confirmPassword)
    case 'email':
      return validateEmailField(form.email)
    case 'emailCode':
      return form.emailCode.trim() ? '' : '请输入邮箱验证码'
    // 选填：留空即不绑定邀请人，填了才校验格式。
    case 'referralCode': {
      const code = form.referralCode.trim().toUpperCase()
      if (!code) return ''
      return REFERRAL_CODE_PATTERN.test(code) ? '' : '邀请码为 4-16 位字母或数字'
    }
  }
}

const REGISTER_PROFILE_FIELDS: RegisterField[] = ['username', 'password', 'confirmPassword', 'email']

function FieldError({ error }: { error?: string }) {
  if (!error) return null
  return <p className="mt-1.5 text-xs text-red-500">{error}</p>
}

export function ConsoleAuthGate({
  bootstrapped,
  onAuthenticated,
}: {
  bootstrapped: boolean
  onAuthenticated: (user: ConsoleUser) => void
}) {
  const [mode, setMode] = useState<Mode>(bootstrapped ? 'login' : 'bootstrap')
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [setupToken, setSetupToken] = useState('')
  const [loading, setLoading] = useState(false)
  const [registerOpen, setRegisterOpen] = useState(false)
  const [registerForm, setRegisterForm] = useState<RegisterForm>(initialRegisterForm)
  const [registerErrors, setRegisterErrors] = useState<RegisterErrors>({})
  const [registerLoading, setRegisterLoading] = useState(false)
  const [codeSending, setCodeSending] = useState(false)
  const [codeCooldown, setCodeCooldown] = useState(0)
  const [forgotOpen, setForgotOpen] = useState(false)
  const [forgotForm, setForgotForm] = useState<ForgotForm>(emptyForgotForm)
  const [forgotLoading, setForgotLoading] = useState(false)
  const [forgotCodeSending, setForgotCodeSending] = useState(false)
  const [forgotCooldown, setForgotCooldown] = useState(0)
  const isBootstrap = mode === 'bootstrap'

  useEffect(() => {
    if (!codeCooldown) return
    const timer = window.setTimeout(() => setCodeCooldown((value) => Math.max(0, value - 1)), 1000)
    return () => window.clearTimeout(timer)
  }, [codeCooldown])

  useEffect(() => {
    if (!forgotCooldown) return
    const timer = window.setTimeout(() => setForgotCooldown((value) => Math.max(0, value - 1)), 1000)
    return () => window.clearTimeout(timer)
  }, [forgotCooldown])

  function updateRegisterForm(patch: Partial<RegisterForm>) {
    setRegisterForm((current) => {
      const next = { ...current, ...patch }
      // 已经显示的错误随输入实时复核，改对了立即消除；没显示过错误的
      // 字段等失焦时再校验，避免边输入边报错。
      setRegisterErrors((errors) => {
        const updated = { ...errors }
        for (const field of Object.keys(patch) as RegisterField[]) {
          if (updated[field]) updated[field] = validateRegisterField(field, next) || undefined
        }
        if (patch.password !== undefined && updated.confirmPassword) {
          updated.confirmPassword = validateRegisterField('confirmPassword', next) || undefined
        }
        return updated
      })
      return next
    })
  }

  function blurRegisterField(field: RegisterField) {
    setRegisterForm((current) => {
      setRegisterErrors((errors) => ({
        ...errors,
        [field]: validateRegisterField(field, current) || undefined,
      }))
      return current
    })
  }

  // 用户名失焦：格式合法后再向服务端确认唯一性。查询失败不拦截，
  // 发送验证码和提交注册时服务端仍会兜底校验。
  async function blurRegisterUsername() {
    blurRegisterField('username')
    const value = registerForm.username.trim()
    if (validateUsernameField(value)) return
    try {
      const response = await apiPost<UsernameCheckResponse>(
        '/auth/username-check',
        { username: value },
        { silent: true },
      )
      if (!response.available) {
        setRegisterErrors((errors) => ({ ...errors, username: '用户名已存在' }))
      }
    } catch {
      // ignore — server-side checks still guard the actual submission
    }
  }

  // 用户名/密码/确认密码/邮箱全部合法后才允许发送验证码。
  const registerProfileValid = REGISTER_PROFILE_FIELDS.every(
    (field) => !validateRegisterField(field, registerForm),
  )

  function closeRegisterModal() {
    if (registerLoading || codeSending) return
    setRegisterOpen(false)
    setRegisterForm(initialRegisterForm())
    setRegisterErrors({})
    setCodeCooldown(0)
  }

  async function submit() {
    if (!username.trim() || !password) {
      message.error('请输入用户名和密码')
      return
    }
    if (mode === 'bootstrap' && !isValidConsolePassword(password)) {
      message.error(CONSOLE_PASSWORD_RULE_MESSAGE)
      return
    }
    if (mode === 'bootstrap' && !setupToken.trim()) {
      message.error('请输入初始化令牌（打印在服务器启动日志中）')
      return
    }
    setLoading(true)
    try {
      if (mode === 'bootstrap') {
        await apiPost<AuthResponse>('/auth/bootstrap', {
          username,
          password,
          setupToken: setupToken.trim(),
        })
      }
      const loggedIn = await apiPost<AuthResponse>('/auth/login', { username, password })
      onAuthenticated(loggedIn.user)
    } finally {
      setLoading(false)
    }
  }

  async function sendRegisterCode() {
    // 把四个资料字段的校验结果全部落到输入框下方再拦截。
    const errors: RegisterErrors = {}
    for (const field of REGISTER_PROFILE_FIELDS) {
      const error = validateRegisterField(field, registerForm)
      if (error) errors[field] = error
    }
    if (Object.keys(errors).length) {
      setRegisterErrors((current) => ({ ...current, ...errors }))
      return
    }
    setCodeSending(true)
    try {
      // 携带用户名：服务端校验用户名是否已存在，并把验证码绑定到 用户名+邮箱。
      const response = await apiPost<RegisterCodeResponse>('/auth/register-code', {
        username: registerForm.username.trim(),
        email: registerForm.email.trim(),
      })
      setCodeCooldown(60)
      if (response.devCode) {
        // 明文验证码不进 toast，只自动填入输入框。
        updateRegisterForm({ emailCode: response.devCode })
        message.success('邮件服务未配置，验证码已自动填入')
      } else {
        message.success('邮箱验证码已发送')
      }
    } finally {
      setCodeSending(false)
    }
  }

  function updateForgotForm(patch: Partial<ForgotForm>) {
    setForgotForm((current) => ({ ...current, ...patch }))
  }

  function closeForgotModal() {
    if (forgotLoading || forgotCodeSending) return
    setForgotOpen(false)
    setForgotForm(emptyForgotForm)
    setForgotCooldown(0)
  }

  async function sendForgotCode() {
    const usernameValue = forgotForm.username.trim()
    const email = forgotForm.email.trim()
    if (!usernameValue || !email) {
      message.warning('请先填写用户名和绑定邮箱')
      return
    }
    setForgotCodeSending(true)
    try {
      // 服务端会校验用户名与绑定邮箱是否匹配，并把验证码绑定到这一对。
      const response = await apiPost<RegisterCodeResponse>('/auth/forgot-password-code', {
        username: usernameValue,
        email,
      })
      setForgotCooldown(60)
      if (response.devCode) {
        // 明文验证码不进 toast，只自动填入输入框。
        updateForgotForm({ emailCode: response.devCode })
        message.success('邮件服务未配置，验证码已自动填入')
      } else {
        message.success('邮箱验证码已发送')
      }
    } finally {
      setForgotCodeSending(false)
    }
  }

  async function submitForgot() {
    const usernameValue = forgotForm.username.trim()
    const emailValue = forgotForm.email.trim()
    const codeValue = forgotForm.emailCode.trim()
    if (!usernameValue || !emailValue || !codeValue) {
      message.warning('请填写用户名、绑定邮箱和验证码')
      return
    }
    setForgotLoading(true)
    try {
      const response = await apiPost<ForgotPasswordResponse>('/auth/forgot-password', {
        username: usernameValue,
        email: emailValue,
        emailCode: codeValue,
      })
      setForgotOpen(false)
      setForgotForm(emptyForgotForm)
      setForgotCooldown(0)
      setUsername(usernameValue)
      if (response.devPassword) {
        // 明文密码不进 toast，只自动填入密码框。
        setPassword(response.devPassword)
        message.success('邮件服务未配置，新密码已自动填入密码框')
      } else {
        setPassword('')
        message.success('新密码已发送至绑定邮箱，请查收后登录')
      }
    } finally {
      setForgotLoading(false)
    }
  }

  async function submitRegister() {
    const errors: RegisterErrors = {}
    for (const field of [...REGISTER_PROFILE_FIELDS, 'emailCode', 'referralCode'] as RegisterField[]) {
      const error = validateRegisterField(field, registerForm)
      if (error) errors[field] = error
    }
    if (Object.keys(errors).length) {
      setRegisterErrors((current) => ({ ...current, ...errors }))
      return
    }
    setRegisterLoading(true)
    const referralCode = registerForm.referralCode.trim().toUpperCase()
    try {
      const response = await apiPost<AuthResponse>('/auth/register', {
        username: registerForm.username.trim(),
        password: registerForm.password,
        email: registerForm.email.trim(),
        emailCode: registerForm.emailCode.trim(),
        ...(referralCode ? { ref: referralCode } : {}),
      })
      // 绑定已在服务端建立，本地这份一次性凭据不再需要。
      clearStoredReferral()
      message.success('注册成功，免费体验 15 天')
      onAuthenticated(response.user)
    } finally {
      setRegisterLoading(false)
    }
  }

  return (
    <div className="min-h-screen w-screen bg-[#eaf0f4] flex flex-col items-center px-4 py-8 font-sans">
      <div className="flex w-full flex-1 items-center justify-center">
        <div className="grid w-full max-w-[1120px] overflow-hidden rounded-xl bg-white shadow-[0_24px_70px_rgba(15,23,42,0.14)] md:grid-cols-[1fr_1fr]">
        <section className="relative flex min-h-[520px] flex-col justify-between overflow-hidden bg-gradient-to-br from-[#20c65a] via-[#18b84f] to-[#15963f] px-8 py-8 text-white sm:px-12 md:px-14">
          <div className="absolute -right-24 -top-24 h-64 w-64 rounded-full bg-white/10 blur-2xl" />
          <div className="absolute -bottom-20 left-12 h-52 w-52 rounded-full bg-emerald-900/15 blur-2xl" />

          <div className="relative">
            <div className="mb-10 flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-white/15 ring-1 ring-white/20">
                <Bot className="h-7 w-7" />
              </div>
              <div>
                <div className="text-lg font-semibold tracking-wide">WxProto</div>
                <div className="mt-1 text-sm text-white/70">你的微信智能助手</div>
              </div>
            </div>

            <h1 className="max-w-[520px] text-4xl font-bold leading-tight tracking-tight sm:text-[42px]">
              一个工作台，
              <br />
              管好你的所有微信
            </h1>
            <p className="mt-8 max-w-[520px] text-base leading-8 text-white/72">
              把微信接进来，聊天集中回复，自动回复、定时发送、群管理一站搞定，让日常微信沟通更轻松。
            </p>
          </div>

          <div className="relative space-y-5 text-white/78">
            <Feature icon={<UsersRound className="h-5 w-5" />} text="多个微信同时在线，一键切换" />
            <Feature icon={<MessageCircle className="h-5 w-5" />} text="消息集中处理，不错过任何好友和群聊" />
            <Feature icon={<Bot className="h-5 w-5" />} text="自动回复、定时发送、群欢迎语，解放双手" />
          </div>
        </section>

        <section className="flex min-h-[520px] items-center justify-center px-8 py-10 sm:px-14">
          <form
            className="w-full max-w-[420px]"
            onSubmit={(event) => {
              event.preventDefault()
              void submit()
            }}
          >
            <div className="mb-8">
              <div className="mb-4 inline-flex items-center gap-2 rounded-lg bg-emerald-50 px-3 py-1.5 text-sm font-semibold text-accent">
                <ShieldCheck className="h-4 w-4" />
                {isBootstrap ? '首次使用' : '账号登录'}
              </div>
              <h2 className="text-3xl font-bold tracking-tight text-ink">
                {isBootstrap ? '创建管理员账号' : '欢迎回来'}
              </h2>
              {isBootstrap ? (
                <p className="mt-3 text-sm leading-6 text-muted">
                  先创建一个管理员账号，之后用它登录并添加你的微信。
                </p>
              ) : null}
            </div>

            <label className="mb-2 block text-sm font-semibold text-soft">用户名</label>
            <div className="mb-5 flex h-[52px] items-center rounded-lg border border-hair bg-white px-4 shadow-[0_1px_2px_rgba(15,23,42,0.04)] transition focus-within:border-accent focus-within:ring-2 focus-within:ring-accent/20">
              <User className="mr-3 h-5 w-5 text-gray-400" />
              <input
                className="h-full min-w-0 flex-1 border-0 bg-transparent text-sm text-ink outline-none placeholder:text-gray-400"
                value={username}
                placeholder="请输入用户名"
                onChange={(event) => setUsername(event.target.value)}
                autoComplete="username"
              />
            </div>

            <label className="mb-2 block text-sm font-semibold text-soft">密码</label>
            <div className="mb-6 flex h-[52px] items-center rounded-lg border border-hair bg-white px-4 shadow-[0_1px_2px_rgba(15,23,42,0.04)] transition focus-within:border-accent focus-within:ring-2 focus-within:ring-accent/20">
              <Lock className="mr-3 h-5 w-5 text-gray-400" />
              <PasswordInput
                wrapperClassName="h-full min-w-0 flex-1"
                className="h-full w-full border-0 bg-transparent text-sm text-ink outline-none placeholder:text-gray-400"
                value={password}
                placeholder={isBootstrap ? PASSWORD_PLACEHOLDER : '请输入密码'}
                onChange={(event) => setPassword(event.target.value)}
                autoComplete={isBootstrap ? 'new-password' : 'current-password'}
              />
            </div>

            {isBootstrap ? (
              <>
                <label className="mb-2 block text-sm font-semibold text-soft">初始化令牌</label>
                <div className="mb-2 flex h-[52px] items-center rounded-lg border border-hair bg-white px-4 shadow-[0_1px_2px_rgba(15,23,42,0.04)] transition focus-within:border-accent focus-within:ring-2 focus-within:ring-accent/20">
                  <KeyRound className="mr-3 h-5 w-5 text-gray-400" />
                  <input
                    className="h-full min-w-0 flex-1 border-0 bg-transparent text-sm text-ink outline-none placeholder:text-gray-400"
                    value={setupToken}
                    placeholder="请输入初始化令牌"
                    onChange={(event) => setSetupToken(event.target.value)}
                    autoComplete="off"
                  />
                </div>
                <p className="mb-6 text-xs leading-5 text-muted">
                  令牌打印在服务器启动日志中，也可通过 BOOTSTRAP_SETUP_TOKEN 环境变量指定。
                </p>
              </>
            ) : null}

            <button
              className="flex h-[52px] w-full items-center justify-center rounded-lg bg-accent text-base font-semibold text-white shadow-[0_12px_24px_rgba(7,193,96,0.26)] transition hover:bg-accent-hover disabled:cursor-not-allowed disabled:opacity-60"
              type="submit"
              disabled={loading}
            >
              {loading ? '处理中...' : isBootstrap ? '创建并登录' : '立即登录'}
            </button>

            <div className="mt-7 flex items-center justify-center gap-2 text-sm text-muted">
              <CheckCircle2 className="h-4 w-4 text-accent" />
              <span>{isBootstrap ? '创建完成后自动进入工作台' : '新用户可免费试用 15 天'}</span>
            </div>

            {!bootstrapped ? (
              <button
                className="mx-auto mt-5 flex items-center gap-2 text-sm font-semibold text-accent hover:text-accent-hover"
                type="button"
                onClick={() => setMode(isBootstrap ? 'login' : 'bootstrap')}
              >
                <User className="h-4 w-4" />
                {isBootstrap ? '已有账号，返回登录' : '首次使用，创建管理员账号'}
              </button>
            ) : (
              <div className="mt-5 flex items-center justify-center gap-6">
                <button
                  className="flex items-center gap-2 text-sm font-semibold text-accent hover:text-accent-hover"
                  type="button"
                  onClick={() => setRegisterOpen(true)}
                >
                  <Mail className="h-4 w-4" />
                  没有账号？免费试用 15 天
                </button>
                <button
                  className="text-sm text-muted transition-colors hover:text-ink"
                  type="button"
                  onClick={() => setForgotOpen(true)}
                >
                  忘记密码？
                </button>
              </div>
            )}

            <div className="mt-6 border-t border-hair pt-4 text-center text-xs text-muted">
              <span className="mr-1">遇到问题？联系客服</span>
              <span className="mr-2">
                QQ：<CopyableSupportNumber value={SUPPORT_QQ} label="客服 QQ" />
              </span>
              <span>
                微信：<CopyableSupportNumber value={SUPPORT_WECHAT_ID} label="客服微信" />
              </span>
            </div>
          </form>
        </section>
        </div>
      </div>

      <footer className="mt-8 text-xs text-muted">
        <a
          href="https://beian.miit.gov.cn"
          target="_blank"
          rel="noreferrer"
          className="transition-colors hover:text-ink"
        >
          蜀ICP备2026038080号-2
        </a>
      </footer>

      {registerOpen ? (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="trial-register-title"
          className="fixed inset-0 z-overlay flex items-center justify-center bg-black/35 px-4 py-6"
        >
          <form
            className="w-full max-w-md overflow-hidden rounded-2xl border border-hair bg-white shadow-card"
            onSubmit={(event) => {
              event.preventDefault()
              void submitRegister()
            }}
          >
            <div className="flex items-center justify-between border-b border-hair px-6 py-4">
              <h3 id="trial-register-title" className="text-base font-semibold text-ink">
                注册免费试用
              </h3>
              <button
                type="button"
                className="text-muted transition-colors hover:text-ink disabled:cursor-not-allowed disabled:opacity-60"
                onClick={closeRegisterModal}
                disabled={registerLoading || codeSending}
                aria-label="关闭注册弹窗"
              >
                <X size={20} />
              </button>
            </div>

            <div className="space-y-4 p-6">
              <div className="flex items-center gap-2 rounded-lg bg-emerald-50 px-3 py-2 text-xs text-accent">
                <Clock3 className="h-4 w-4 shrink-0" />
                完成邮箱验证即可开通，免费体验全部功能 15 天。
              </div>

              <label className="block">
                <span className="mb-1.5 block text-sm text-muted">用户名</span>
                <input
                  className="input"
                  value={registerForm.username}
                  onChange={(event) => updateRegisterForm({ username: event.target.value })}
                  onBlur={() => void blurRegisterUsername()}
                  placeholder={USERNAME_PLACEHOLDER}
                  autoComplete="username"
                  autoFocus
                />
                <FieldError error={registerErrors.username} />
              </label>

              <label className="block">
                <span className="mb-1.5 block text-sm text-muted">密码</span>
                <PasswordInput
                  className="input w-full"
                  value={registerForm.password}
                  onChange={(event) => updateRegisterForm({ password: event.target.value })}
                  onBlur={() => blurRegisterField('password')}
                  placeholder={PASSWORD_PLACEHOLDER}
                  autoComplete="new-password"
                />
                <FieldError error={registerErrors.password} />
              </label>

              <label className="block">
                <span className="mb-1.5 block text-sm text-muted">确认密码</span>
                <PasswordInput
                  className="input w-full"
                  value={registerForm.confirmPassword}
                  onChange={(event) => updateRegisterForm({ confirmPassword: event.target.value })}
                  onBlur={() => blurRegisterField('confirmPassword')}
                  placeholder="再次输入密码"
                  autoComplete="new-password"
                />
                <FieldError error={registerErrors.confirmPassword} />
              </label>

              <label className="block">
                <span className="mb-1.5 block text-sm text-muted">邮箱</span>
                <input
                  className="input"
                  value={registerForm.email}
                  type="email"
                  onChange={(event) => updateRegisterForm({ email: event.target.value })}
                  onBlur={() => blurRegisterField('email')}
                  placeholder="name@example.com"
                  autoComplete="email"
                />
                <FieldError error={registerErrors.email} />
              </label>

              <label className="block">
                <span className="mb-1.5 block text-sm text-muted">邮箱验证码</span>
                <div className="flex gap-2">
                  <input
                    className="input min-w-0 flex-1"
                    value={registerForm.emailCode}
                    onChange={(event) => updateRegisterForm({ emailCode: event.target.value })}
                    onBlur={() => blurRegisterField('emailCode')}
                    placeholder="6 位验证码"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                  />
                  <button
                    type="button"
                    className="btn-secondary h-9 shrink-0"
                    onClick={() => void sendRegisterCode()}
                    disabled={
                      codeSending ||
                      registerLoading ||
                      codeCooldown > 0 ||
                      !registerProfileValid ||
                      Boolean(registerErrors.username)
                    }
                  >
                    {codeSending ? '发送中' : codeCooldown > 0 ? `${codeCooldown}s` : '发送验证码'}
                  </button>
                </div>
                <FieldError error={registerErrors.emailCode} />
                {!registerProfileValid ? (
                  <span className="mt-1.5 block text-xs text-muted">
                    正确填写用户名、密码、确认密码和邮箱后即可发送验证码
                  </span>
                ) : null}
              </label>

              <label className="block">
                <span className="mb-1.5 block text-sm text-muted">邀请码（选填）</span>
                <input
                  className="input uppercase"
                  value={registerForm.referralCode}
                  onChange={(event) => updateRegisterForm({ referralCode: event.target.value.toUpperCase() })}
                  onBlur={() => blurRegisterField('referralCode')}
                  placeholder="有好友邀请码可填写"
                  autoComplete="off"
                />
                <FieldError error={registerErrors.referralCode} />
              </label>
            </div>

            <div className="flex items-center justify-end gap-3 border-t border-hair bg-page px-6 py-4">
              <button
                type="button"
                className="btn-secondary"
                onClick={closeRegisterModal}
                disabled={registerLoading || codeSending}
              >
                取消
              </button>
              <button type="submit" className="btn-primary" disabled={registerLoading || codeSending}>
                {registerLoading ? '注册中...' : '完成注册'}
              </button>
            </div>
          </form>
        </div>
      ) : null}

      {forgotOpen ? (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="forgot-password-title"
          className="fixed inset-0 z-overlay flex items-center justify-center bg-black/35 px-4 py-6"
          onClick={closeForgotModal}
        >
          <form
            className="w-full max-w-md overflow-hidden rounded-2xl border border-hair bg-white shadow-card"
            onClick={(event) => event.stopPropagation()}
            onSubmit={(event) => {
              event.preventDefault()
              void submitForgot()
            }}
          >
            <div className="flex items-center justify-between border-b border-hair px-6 py-4">
              <h3 id="forgot-password-title" className="text-base font-semibold text-ink">
                找回密码
              </h3>
              <button
                type="button"
                className="text-muted transition-colors hover:text-ink disabled:cursor-not-allowed disabled:opacity-60"
                onClick={closeForgotModal}
                disabled={forgotLoading || forgotCodeSending}
                aria-label="关闭找回密码弹窗"
              >
                <X size={20} />
              </button>
            </div>

            <div className="space-y-4 p-6">
              <div className="flex items-center gap-2 rounded-lg bg-emerald-50 px-3 py-2 text-xs text-accent">
                <ShieldCheck className="h-4 w-4 shrink-0" />
                验证通过后，系统会生成一个新的随机密码并发送到你的绑定邮箱。
              </div>

              <label className="block">
                <span className="mb-1.5 block text-sm text-muted">用户名</span>
                <input
                  className="input"
                  value={forgotForm.username}
                  onChange={(event) => updateForgotForm({ username: event.target.value })}
                  placeholder="请输入用户名"
                  autoComplete="username"
                  autoFocus
                />
              </label>

              <label className="block">
                <span className="mb-1.5 block text-sm text-muted">绑定邮箱</span>
                <input
                  className="input"
                  value={forgotForm.email}
                  type="email"
                  onChange={(event) => updateForgotForm({ email: event.target.value })}
                  placeholder="注册或绑定时使用的邮箱"
                  autoComplete="email"
                />
              </label>

              <label className="block">
                <span className="mb-1.5 block text-sm text-muted">邮箱验证码</span>
                <div className="flex gap-2">
                  <input
                    className="input min-w-0 flex-1"
                    value={forgotForm.emailCode}
                    onChange={(event) => updateForgotForm({ emailCode: event.target.value })}
                    placeholder="6 位验证码"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                  />
                  <button
                    type="button"
                    className="btn-secondary h-9 shrink-0"
                    onClick={() => void sendForgotCode()}
                    disabled={forgotCodeSending || forgotLoading || forgotCooldown > 0}
                  >
                    {forgotCodeSending ? '发送中' : forgotCooldown > 0 ? `${forgotCooldown}s` : '发送验证码'}
                  </button>
                </div>
              </label>
            </div>

            <div className="flex items-center justify-end gap-3 border-t border-hair bg-page px-6 py-4">
              <button
                type="button"
                className="btn-secondary"
                onClick={closeForgotModal}
                disabled={forgotLoading || forgotCodeSending}
              >
                取消
              </button>
              <button type="submit" className="btn-primary" disabled={forgotLoading || forgotCodeSending}>
                {forgotLoading ? '重置中...' : '重置密码'}
              </button>
            </div>
          </form>
        </div>
      ) : null}
    </div>
  )
}

function Feature({ icon, text }: { icon: ReactNode; text: string }) {
  return (
    <div className="flex items-center gap-4">
      <div className="flex h-9 w-9 items-center justify-center rounded-full bg-white/12 text-white ring-1 ring-white/10">
        {icon}
      </div>
      <span className="text-sm sm:text-base">{text}</span>
    </div>
  )
}
